import { useMemo } from 'react'
import { MoreHorizontal } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import { Button } from '@/shared/components/ui/button'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { Badge } from '@/shared/components/ui/badge'
import { StatusBadge } from '@/shared/components/ui/status-badge'
import { CurrencyDisplay } from '@/shared/components/ui/currency-display'
import { DateDisplay } from '@/shared/components/ui/date-display'
import { usePermissions } from '@/shared/lib/permissions/use-permissions'
import { Permissions, PermissionLevel } from '@/shared/lib/permissions/permission-values'
import type { BankAccount, BankAccountFilterValues } from '../types/bank-account'

interface ColumnOptions {
  filterValues: BankAccountFilterValues | undefined
  onEdit: (account: BankAccount) => void
  onDelete: (account: BankAccount) => void
  onUpdateBalance: (account: BankAccount) => void
  onUploadStatement: (account: BankAccount) => void
  onReconnect: (account: BankAccount) => void
}

export function useBankAccountColumns({
  filterValues,
  onEdit, onDelete, onUpdateBalance, onUploadStatement, onReconnect,
}: ColumnOptions): ColumnDef<BankAccount>[] {
  const { can } = usePermissions()
  const canEdit = can({ name: Permissions.BankAccounts, level: PermissionLevel.CreateEdit })
  const canUploadStatement = can({ name: Permissions.BankStatements, level: PermissionLevel.CreateEdit })

  return useMemo<ColumnDef<BankAccount>[]>(() => [
    {
      accessorKey: 'id',
      header: 'ID',
      size: 80,
      enableSorting: true,
      enableColumnFilter: true,
      meta: { filterVariant: 'number' },
      cell: ({ getValue }) => <span className="font-mono text-xs">{String(getValue() ?? '')}</span>,
    },
    {
      accessorKey: 'accountName',
      header: 'Account Name',
      size: 200,
      enableSorting: true,
      enableColumnFilter: true,
      meta: { filterVariant: 'text' },
    },
    {
      accessorKey: 'currencyIsoCode',
      header: 'CCY',
      size: 70,
      enableSorting: true,
      enableColumnFilter: true,
      meta: { filterVariant: 'set', filterStaticOptions: filterValues?.currencies.map(o => o.Label) ?? [] },
    },
    {
      accessorKey: 'institutionName',
      header: 'Institution',
      size: 160,
      enableSorting: true,
      enableColumnFilter: true,
      meta: { filterVariant: 'set', filterStaticOptions: filterValues?.institutions.map(o => o.Label) ?? [] },
    },
    {
      accessorKey: 'companyName',
      header: 'Company',
      size: 150,
      enableSorting: true,
      enableColumnFilter: true,
      meta: { filterVariant: 'set', filterStaticOptions: filterValues?.companies.map(o => o.Label) ?? [] },
    },
    {
      accessorKey: 'iban',
      header: 'IBAN',
      size: 200,
      enableSorting: true,
      enableColumnFilter: true,
      meta: { filterVariant: 'text' },
      cell: ({ getValue }) => (
        <span className="font-mono text-xs">{String(getValue() ?? '')}</span>
      ),
    },
    {
      accessorKey: 'accountNumber',
      header: 'Account Number',
      size: 140,
      enableSorting: true,
      enableColumnFilter: true,
      meta: { filterVariant: 'text' },
      cell: ({ getValue }) => (
        <span className="font-mono text-xs">{String(getValue() ?? '')}</span>
      ),
    },
    {
      accessorKey: 'closingBalance',
      header: 'Closing Balance',
      size: 130,
      enableSorting: false,
      enableColumnFilter: false,
      meta: { align: 'right' },
      cell: ({ row }) =>
        row.original.closingBalance !== undefined ? (
          <CurrencyDisplay amount={row.original.closingBalance} colored />
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      accessorKey: 'closingBalanceDate',
      header: 'Closing Balance Date',
      size: 120,
      enableSorting: false,
      cell: ({ getValue }) => <DateDisplay value={getValue() as string} />,
    },
    {
      accessorKey: 'connectivity',
      header: 'Connectivity',
      size: 130,
      enableSorting: true,
      enableColumnFilter: true,
      meta: { filterVariant: 'set', filterStaticOptions: filterValues?.connectivity.map(o => o.Label) ?? [] },
    },
    {
      accessorKey: 'connectionStatus',
      header: 'Connection',
      size: 120,
      enableSorting: true,
      enableColumnFilter: true,
      meta: { filterVariant: 'set', filterStaticOptions: ['Connected', 'Disconnected'] },
      cell: ({ getValue }) => {
        const v = getValue() as string
        return <StatusBadge status={v === 'Connected' ? 'connected' : 'disconnected'} />
      },
    },
    {
      /*
       * Whether this account can pay, without having to open it and find out. Before this column the
       * only way to know was to start a payment and be told no — and the answer arrived under a heading
       * ("No payment integration") on a screen the person had already committed to.
       */
      accessorKey: 'dispersesThrough',
      header: 'Payments',
      size: 140,
      enableSorting: true,
      enableColumnFilter: false,
      cell: ({ row }) => {
        const rule = row.original.dispersesThrough
        const enabled = row.original.paymentsEnabled !== false

        // Two different nos, and they are mended in different places: no channel is a setup somebody
        // still has to choose, a switched-off account is one press on its own form.
        if (rule === null || rule === undefined || rule === '') {
          return <span className="text-muted-foreground">—</span>
        }

        return (
          <Badge variant={enabled ? 'default' : 'outline'} className="font-normal">
            {rule}
          </Badge>
        )
      },
    },
    {
      accessorKey: 'active',
      header: 'Status',
      size: 90,
      enableSorting: true,
      enableColumnFilter: true,
      meta: { filterVariant: 'boolean' },
      cell: ({ getValue }) => <StatusBadge status={getValue() as boolean} />,
    },
    {
      accessorKey: 'lastBankUpdate',
      header: 'Last Update',
      size: 180,
      enableSorting: true,
      cell: ({ getValue }) => <DateDisplay value={getValue() as string} format="datetime-seconds" />,
    },
    {
      accessorKey: 'availableBalance',
      header: 'Available Balance',
      size: 130,
      enableSorting: false,
      enableColumnFilter: false,
      meta: { align: 'right' },
      // Backend sends explicit null when the account has no eligible balance
      // for today — must render empty, never 0.
      cell: ({ row }) =>
        row.original.availableBalance != null ? (
          <CurrencyDisplay amount={row.original.availableBalance} colored />
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      size: 200,
      enableHiding: true,
      enableColumnFilter: true,
      meta: { filterVariant: 'text', defaultHidden: true },
      cell: ({ getValue }) => {
        const v = getValue() as string | undefined
        return v ? <span className="truncate block max-w-[200px]" title={v}>{v}</span> : <span className="text-muted-foreground">—</span>
      },
    },
    {
      accessorKey: 'bic',
      header: 'BIC',
      size: 120,
      enableHiding: true,
      meta: { defaultHidden: true },
      cell: ({ getValue }) => {
        const v = getValue() as string | undefined
        return v ? <span className="font-mono text-xs">{v}</span> : <span className="text-muted-foreground">—</span>
      },
    },
    {
      accessorKey: 'ownerName',
      header: 'Owner Name',
      size: 160,
      enableHiding: true,
      enableColumnFilter: true,
      meta: { filterVariant: 'text', defaultHidden: true },
    },
    {
      accessorKey: 'companyGroupName',
      header: 'Company Group',
      size: 160,
      enableHiding: true,
      meta: { defaultHidden: true },
    },
    {
      accessorKey: 'createdOn',
      header: 'Created On',
      size: 120,
      enableHiding: true,
      meta: { defaultHidden: true },
      cell: ({ getValue }) => <DateDisplay value={getValue() as string} />,
    },
    {
      accessorKey: 'openBankingAccessExpiryDate',
      header: 'OB Expiry',
      size: 120,
      enableHiding: true,
      meta: { defaultHidden: true },
      cell: ({ getValue }) => <DateDisplay value={getValue() as string} />,
    },
    {
      accessorKey: 'openBankingDaysUntilExpiry',
      header: 'OB Days Left',
      size: 110,
      enableHiding: true,
      meta: { defaultHidden: true },
      cell: ({ getValue }) => {
        const v = getValue() as number | undefined
        return v != null ? <span className="tabular-nums">{v}</span> : <span className="text-muted-foreground">—</span>
      },
    },
    {
      accessorKey: 'openBankingStatusName',
      header: 'OB Status',
      size: 110,
      enableHiding: true,
      meta: { defaultHidden: true },
    },
    {
      accessorKey: 'format',
      header: 'Format',
      size: 100,
      enableHiding: true,
      meta: { defaultHidden: true },
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 80,
      enableSorting: false,
      enableColumnFilter: false,
      enableHiding: false,
      cell: ({ row }) => {
        const account = row.original
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">Actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canEdit && (
                <DropdownMenuItem onClick={() => onEdit(account)}>Edit</DropdownMenuItem>
              )}
              {canEdit && !account.isMultiCurrency && (
                <DropdownMenuItem onClick={() => onUpdateBalance(account)}>
                  Update Balance
                </DropdownMenuItem>
              )}
              {canUploadStatement && !account.hasParent && (
                <DropdownMenuItem onClick={() => onUploadStatement(account)}>
                  Upload Statement
                </DropdownMenuItem>
              )}
              {canEdit && account.canReconnect && (
                <DropdownMenuItem onClick={() => onReconnect(account)}>
                  Reconnect Open Banking
                </DropdownMenuItem>
              )}
              {canEdit && <DropdownMenuSeparator />}
              {canEdit && (
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => onDelete(account)}
                >
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
   
  ], [canEdit, canUploadStatement, filterValues, onDelete, onEdit, onReconnect, onUpdateBalance, onUploadStatement])
}
